// The ONE unread-notifications listener on the web (cross-check.md F4).
//
// Query: notifications where read == false && user_id == uid orderBy created_at desc,
// with NO limit, exactly like mobile contexts/UnreadChatsContext.js (a limit would
// undercount the chats badge). Index: notifications(read, user_id, created_at DESC).
//
// UnreadProvider (context/UnreadContext.jsx) is the only caller of
// useNotificationsSnapshot(). Everyone else reads the shared feed:
//   const feed = useNotificationsFeed();   // null outside UnreadProvider
//   feed.docs      unread docs [{ id, ...data }], newest first
//   feed.version   bumps on every applied snapshot (0 = nothing yet)
//   feed.error     last listener error (docs keep the last snapshot), or null. Firestore ends a
//                  listener on error, so it is re-opened with capped backoff (and again when the
//                  tab comes back into view after the retries ran out); error clears on the next
//                  snapshot. While set, counts derived from docs are stale, not zero.
//   feed.onChanges(cb) -> unsubscribe
//       cb({ changes, docs, version, initial }) for every snapshot, where
//       changes = [{ type: "added"|"modified"|"removed", doc }] (Firestore docChanges).
//       A subscriber that registers after data arrived gets one replay right away:
//       every current doc as "added" with initial: true, which is what mobile's own
//       first snapshot looks like. The first snapshot after a re-open is also sent with
//       initial: true and every doc as "added" (removals during the outage are not reported),
//       so a subscriber must rebuild its state from `docs` when `initial` is set.
//       The Chats provider (C1) ports mobile's docChanges logic on top of this and must NOT
//       open a second listener on this query.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { db, COLLECTIONS } from "../firebase.web";

export const NotificationsFeedContext = createContext(null);

export const useNotificationsFeed = () => useContext(NotificationsFeedContext);

const EMPTY = { docs: [], version: 0, error: null };
const RETRY_MAX = 6; // re-opens after an error before waiting for the tab to be shown again

export function useNotificationsSnapshot(uid) {
  const [state, setState] = useState(EMPTY);
  const subsRef = useRef(new Set());
  const latestRef = useRef(EMPTY);

  useEffect(() => {
    latestRef.current = EMPTY;
    setState(EMPTY);
    if (!uid) return undefined;
    let alive = true;
    let version = 0; // keeps counting across re-opens, so notificationsVersion never repeats
    let unsubscribe = null;
    let retryTimer = null;
    let retries = 0;
    const q = query(
      collection(db, COLLECTIONS.NOTIFICATIONS),
      where("read", "==", false),
      where("user_id", "==", uid),
      orderBy("created_at", "desc")
    );
    const subscribe = () => {
      if (!alive) return;
      let first = true; // first snapshot of THIS listener: a full replay for subscribers
      unsubscribe = onSnapshot(
        q,
        (snap) => {
          if (!alive) return;
          retries = 0;
          version += 1;
          const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          const changes = snap.docChanges().map((c) => ({ type: c.type, doc: { id: c.doc.id, ...c.doc.data() } }));
          const next = { docs, version, error: null };
          latestRef.current = next;
          setState(next);
          const event = { changes, docs, version, initial: first };
          first = false;
          subsRef.current.forEach((cb) => {
            try { cb(event); } catch (e) { if (__DEV__) console.error("notifications onChanges subscriber failed:", e); }
          });
        },
        (error) => {
          if (__DEV__) console.error("notifications listener error:", error);
          if (!alive) return;
          // Keep the last snapshot: a listener error is never shown as "no notifications".
          const next = { ...latestRef.current, error };
          latestRef.current = next;
          setState(next);
          // Firestore ended this listener: re-open it with backoff (5 s, 10 s, ... capped at 60 s).
          if (typeof unsubscribe === "function") unsubscribe();
          unsubscribe = null;
          if (retries >= RETRY_MAX) return;
          retryTimer = setTimeout(() => { retryTimer = null; subscribe(); }, Math.min(60000, 5000 * 2 ** retries));
          retries += 1;
        }
      );
    };
    subscribe();
    // Retries ran out: try once more each time the tab comes back into view.
    const onVisible = () => {
      if (!alive || document.visibilityState !== "visible") return;
      if (unsubscribe || retryTimer) return;
      retries = 0;
      subscribe();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
      if (retryTimer) clearTimeout(retryTimer);
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [uid]);

  const onChanges = useCallback((cb) => {
    if (typeof cb !== "function") return () => {};
    subsRef.current.add(cb);
    const cur = latestRef.current;
    if (cur.version > 0) {
      cb({ changes: cur.docs.map((doc) => ({ type: "added", doc })), docs: cur.docs, version: cur.version, initial: true });
    }
    return () => { subsRef.current.delete(cb); };
  }, []);

  return useMemo(() => ({ ...state, onChanges }), [state, onChanges]);
}
